// ─── useStickToBottom — chat auto-scroll that follows live output but yields to the user ──
//
// The behavior contract (agreed with the user):
//   • At the bottom        → STICK: every time content grows (a streamed token, a late image
//                            load, markdown reflow, a slow WS frame) we re-pin to the bottom, so
//                            the latest line is always visible. Auto-push.
//   • Scrolled up to read  → FREE: never yanked down. New content just appends below; the ↓
//                            button appears so the user can come back when they want.
//
// Why the old approach fought the user: it inferred "should I stick?" purely from scroll
// POSITION (within 120px of the bottom) and re-pinned on a few capped timers (≤450ms). During
// a fast stream the position never escaped the 120px zone before the next pin yanked it back —
// so scrolling up meant out-running the stream. And on open, content settled AFTER the 450ms
// window, so it landed mid-scroll.
//
// The fix: drive STICK from user INTENT, and follow growth with a ResizeObserver (no time cap):
//   • disengage  the instant the user gestures UP (wheel up / drag down / PageUp/Home) —
//                position-independent, so one flick releases it (no out-running the stream).
//   • re-engage  only when the user returns to the bottom themselves (or taps ↓ / sends).
//   • follow     a ResizeObserver on the content keeps us pinned while stuck, until it settles —
//                covers images/markdown/late frames that the timed re-pins missed.
// Our own programmatic scrolls are flagged so they can't be mistaken for the user scrolling.

import { useRef, useState, useEffect, useLayoutEffect, useCallback } from 'react'

const RE_ENGAGE_PX = 48  // user scrolls back to within this of the bottom → resume following
const SHOW_BTN_PX = 120  // farther than this from the bottom → show the "scroll to latest" button

export interface StickToBottom {
  /** attach to the scroll container (overflow-y:auto element). */
  scrollRef: React.RefObject<HTMLDivElement>
  /** attach to an inner wrapper that holds ALL scrollable content (observed for growth). */
  contentRef: React.RefObject<HTMLDivElement>
  /** show the floating "↓ scroll to latest" button (user is reading above). */
  showScrollDown: boolean
  /** force re-engage + jump to the bottom (the ↓ button, sending a message). */
  goToBottom: () => void
  /** attach to the container's onScroll — manages the button + re-engages near the bottom. */
  handleScroll: () => void
  /** true while following the bottom. Set to false in lazy-load (prepend) so it won't yank down. */
  stickRef: React.MutableRefObject<boolean>
  /** run a scroll mutation flagged as non-user (e.g. restoring the anchor after prepending). */
  runProgrammatic: (fn: () => void) => void
}

export function useStickToBottom(resetKey: string): StickToBottom {
  const scrollRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const stickRef = useRef(true)              // start stuck (open lands at the latest)
  const programmatic = useRef(false)         // true while WE are setting scrollTop
  const [showScrollDown, setShowScrollDown] = useState(false)

  const scrollToBottomNow = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    programmatic.current = true
    // Force an INSTANT jump: .chat-turns (Clean²) sets `scroll-behavior: smooth`, which turns
    // scrollTop into an ANIMATION. On open / view-switch the content settles in several steps, so
    // a smooth pin can be interrupted mid-animation and land at the middle (the "บางครั้งกลาง" on
    // Clean² but not Term²). Override to auto for the jump, then restore.
    const prevBehavior = el.style.scrollBehavior
    el.style.scrollBehavior = 'auto'
    el.scrollTop = el.scrollHeight
    el.style.scrollBehavior = prevBehavior
    // the scroll event from the line above is dispatched async — clear AFTER it flushes so
    // handleScroll ignores it (our pin must not be read as the user scrolling).
    requestAnimationFrame(() => { programmatic.current = false })
  }, [])

  const runProgrammatic = useCallback((fn: () => void) => {
    programmatic.current = true
    fn()
    requestAnimationFrame(() => { programmatic.current = false })
  }, [])

  const goToBottom = useCallback(() => {
    stickRef.current = true
    setShowScrollDown(false)
    scrollToBottomNow()
  }, [scrollToBottomNow])

  // Reset on chat/view change (resetKey): start stuck + pin. Content isn't loaded yet, so the
  // ResizeObserver below keeps re-pinning as it streams in — no timed-guess needed.
  useLayoutEffect(() => {
    stickRef.current = true
    setShowScrollDown(false)
    scrollToBottomNow()
  }, [resetKey, scrollToBottomNow])

  // User-intent listeners on the scroll container: an upward gesture disengages IMMEDIATELY,
  // regardless of position — this is what stops the stream from fighting a scroll-up.
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const disengage = () => { stickRef.current = false }
    const onWheel = (e: WheelEvent) => { if (e.deltaY < 0) disengage() }
    let touchY = 0
    const onTouchStart = (e: TouchEvent) => { touchY = e.touches[0]?.clientY ?? 0 }
    const onTouchMove = (e: TouchEvent) => {
      const y = e.touches[0]?.clientY ?? 0
      if (y > touchY + 2) disengage()   // finger moving DOWN = content scrolls UP = reading up
      touchY = y
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'PageUp' || e.key === 'Home' || e.key === 'ArrowUp') disengage()
    }
    el.addEventListener('wheel', onWheel, { passive: true })
    el.addEventListener('touchstart', onTouchStart, { passive: true })
    el.addEventListener('touchmove', onTouchMove, { passive: true })
    el.addEventListener('keydown', onKey)
    return () => {
      el.removeEventListener('wheel', onWheel)
      el.removeEventListener('touchstart', onTouchStart)
      el.removeEventListener('touchmove', onTouchMove)
      el.removeEventListener('keydown', onKey)
    }
  }, [])

  // Container onScroll: ignore our own pins; otherwise update the button + re-engage when the
  // user has scrolled back to the bottom themselves.
  const handleScroll = useCallback(() => {
    const el = scrollRef.current
    if (!el || programmatic.current) return
    const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight
    if (distFromBottom <= RE_ENGAGE_PX) stickRef.current = true
    setShowScrollDown(distFromBottom > SHOW_BTN_PX)
  }, [])

  // Follow content growth while stuck (the heart of the fix). Fires on EVERY height change —
  // streamed tokens, image loads, markdown reflow, late frames — until the content settles.
  useEffect(() => {
    const content = contentRef.current
    const el = scrollRef.current
    if (!content || !el) return
    const ro = new ResizeObserver(() => {
      if (stickRef.current) scrollToBottomNow()
      else {
        const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight
        setShowScrollDown(distFromBottom > SHOW_BTN_PX)
      }
    })
    ro.observe(content)
    return () => ro.disconnect()
  }, [scrollToBottomNow])

  return { scrollRef, contentRef, showScrollDown, goToBottom, handleScroll, stickRef, runProgrammatic }
}
